import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface LogoTest {
  id: string;
  program_id: string;
  phone_number: string;
  message_id: string;
  job_id: string;
  text: string;
  locale: string;
  status: 'processing' | 'done' | 'failed' | 'pending';
  logo_image_url?: string;
  polling_attempts: number;
  created_at: string;
  updated_at: string;
}

export interface LogoSubmitRequest {
  program_id: string;
  phone_number: string;
  message_id: string;
  text: string;
  locale?: string;
  user_id: string;
}

export interface LogoSubmitResponse {
  ok: boolean;
  msg: string;
  result: {
    id: string;
    job_id: string;
    status: string;
  };
}

export interface LogoStatusResponse {
  ok: boolean;
  result: {
    id: string;
    job_id: string;
    status: string;
    logo_image_url?: string;
    attempts: number;
    created_at: string;
    updated_at: string;
  };
}

export interface LogoPollResponse {
  ok: boolean;
  msg: string;
  result: {
    id: string;
    job_id: string;
    status: string;
    logo_image_url?: string;
    attempts: number;
    completed_at?: string;
  };
}

export interface LogoListResponse {
  ok: boolean;
  results: LogoTest[];
  total: number;
  page: number;
  limit: number;
}

@Injectable({
  providedIn: 'root',
})
export class LogosService {
  private apiUrl = `${environment.base_url}/logos`;

  constructor(private http: HttpClient) {}

  /**
   * Enviar solicitud de test de logo
   */
  submitLogoTest(request: LogoSubmitRequest): Observable<LogoSubmitResponse> {
    return this.http.post<LogoSubmitResponse>(`${this.apiUrl}/submit`, request);
  }

  /**
   * Obtener estado de un logo
   */
  getLogoStatus(jobId: string): Observable<LogoStatusResponse> {
    return this.http.get<LogoStatusResponse>(`${this.apiUrl}/status/${jobId}`);
  }

  /**
   * Hacer polling de un logo (long-polling)
   * timeout en ms
   */
  pollLogo(
    jobId: string,
    timeout: number = 120000,
  ): Observable<LogoPollResponse> {
    const params = new HttpParams().set('timeout', timeout.toString());
    return this.http.post<LogoPollResponse>(
      `${this.apiUrl}/poll/${jobId}`,
      null,
      { params },
    );
  }

  /**
   * Polling síncrono (para backend directo)
   */
  pollLogoSync(jobId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/poll-sync/${jobId}`, null);
  }

  /**
   * Listar logos de un programa
   */
  getLogosByProgram(
    programId: string,
    page: number = 1,
    limit: number = 10,
    status?: string,
  ): Observable<LogoListResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<LogoListResponse>(
      `${this.apiUrl}/program/${programId}`,
      { params },
    );
  }
}
